import { useCallback, useEffect, ReactNode } from "react";
import {
  useVoiceConversations,
  voiceConversations$,
} from "@jugl-web/rest-api/integrations/hooks/useVoiceConversations";
import {
  CRMChannel,
  VoiceConversationModel,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { VoiceMessageModel } from "@jugl-web/rest-api/integrations/models/VoiceMessage";
import { DataLoadingWrapper } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useInView } from "react-intersection-observer";
import { ConversationListEmptyState } from "../ConversationListEmptyState";
import { VoiceConversationTile } from "../VoiceConversationTile";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useSelectedConversation } from "../../hooks/useSelectedConversation";
import { reactivateClosedConversation } from "../../../../utils/reactivateClosedConversation";
import { shouldUpdateConversationListFromMessage } from "../../../../utils/shouldUpdateConversationListFromMessage";

export interface VoiceConversationsProps {
  searchQuery: string;
}

export const VoiceConversations: React.FC<VoiceConversationsProps> = ({
  searchQuery,
}) => {
  const { t } = useTranslations();
  const { entityId } = useEntitySelectedProvider();
  const { ref: inViewRef, inView } = useInView();
  const { selectedConversation, selectConversation, incomingVoiceMessages$ } =
    useClientSupportPageContext();
  const { integrationsApi } = useRestApiProvider();
  const [fetchConversationById] =
    integrationsApi.useLazyGetVoiceConversationByIdQuery();

  const {
    data: conversations,
    isLoading,
    isLoadingMore,
    isError,
    isLoadingMoreError,
    hasMore,
    retry,
    loadMore,
  } = useVoiceConversations({
    entityId,
    searchTerm: searchQuery,
    pageSize: 10,
  });

  useSelectedConversation({
    channel: CRMChannel.voice,
  });

  const prependConversationById = useCallback(
    async (convId: string) => {
      const response = await fetchConversationById({ entityId, convId });

      if (response && "data" in response && response.data) {
        voiceConversations$.next({
          type: "prepend",
          conversation: response.data,
        });
      }
    },
    [entityId, fetchConversationById]
  );

  const mergeMessageIntoConversation = useCallback(
    (
      conversation: VoiceConversationModel,
      message: VoiceMessageModel & { msg_status?: string }
    ): VoiceConversationModel =>
      reactivateClosedConversation({
        ...conversation,
        ...message,
        contact_info: conversation.contact_info,
        name: conversation.contact_info?.name || conversation.name,
      }),
    []
  );

  const refreshConversationInList = useCallback(
    async (convId: string) => {
      const conversationExists = conversations.some(
        (c) => c.conv_id === convId
      );

      if (!conversationExists) return;

      const response = await fetchConversationById({ entityId, convId });

      if (response && "data" in response && response.data) {
        const conversation = response.data;

        voiceConversations$.next({
          type: "update",
          conversation,
        });

        if (selectedConversation?.conv_id === convId) {
          selectConversation(conversation, true);
        }
      }
    },
    [
      conversations,
      entityId,
      fetchConversationById,
      selectConversation,
      selectedConversation?.conv_id,
    ]
  );

  useEffect(() => {
    const subscription = incomingVoiceMessages$.subscribe((message) => {
      if (message.type === "info" && message.status === "summary") {
        refreshConversationInList(message.conv_id);
        return;
      }

      if (!shouldUpdateConversationListFromMessage(message)) {
        return;
      }

      const conversationExists = conversations.some(
        (c) => c.conv_id === message.conv_id
      );

      if (!conversationExists) {
        prependConversationById(message.conv_id);
        return;
      }

      if (selectedConversation?.conv_id === message.conv_id) {
        if (
          (selectedConversation.msg_id === message.msg_id &&
            (selectedConversation as VoiceConversationModel).payload
              .timestamp >= message.payload.timestamp) ||
          selectedConversation.msg_id !== message.msg_id
        ) {
          const updatedConversation = mergeMessageIntoConversation(
            selectedConversation as VoiceConversationModel,
            message
          );
          voiceConversations$.next({
            type: "update",
            conversation: updatedConversation,
          });
          selectConversation(updatedConversation, true);
        }
      }

      const existingConversation = conversations.find(
        (c) => c.conv_id === message.conv_id
      );

      if (
        existingConversation &&
        existingConversation.payload.timestamp <= message.payload.timestamp &&
        existingConversation.msg_id !== message.msg_id
      ) {
        const updatedConversation = mergeMessageIntoConversation(
          existingConversation,
          message
        );

        voiceConversations$.next({
          type: "prepend",
          conversation: updatedConversation,
        });

        if (selectedConversation?.conv_id === updatedConversation.conv_id) {
          selectConversation(updatedConversation, true);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [
    incomingVoiceMessages$,
    conversations,
    prependConversationById,
    refreshConversationInList,
    selectedConversation,
    selectConversation,
    mergeMessageIntoConversation,
  ]);

  useEffect(() => {
    if (inView && hasMore && !isLoadingMore) {
      loadMore();
    }
  }, [inView, hasMore, isLoadingMore, loadMore]);

  return (
    <DataLoadingWrapper
      isError={isError}
      isLoading={isLoading}
      onRetry={retry}
      isLoadingMore={isLoadingMore}
      isLoadingMoreError={isLoadingMoreError}
    >
      {conversations.map((conversation) => (
        <VoiceConversationTile
          key={conversation.conv_id}
          conversation={conversation}
          searchQuery={searchQuery}
        />
      ))}
      {hasMore && !isLoading && !isLoadingMore && <div ref={inViewRef} />}
      <ConversationListEmptyState
        hasConversations={conversations.length > 0}
        searchQuery={searchQuery}
        emptyMessage={t(
          {
            id: "client-support-page.no-phone-calls-received-yet",
            defaultMessage:
              "No <highlightedText>Phone Calls</highlightedText> received yet",
          },
          {
            highlightedText: (text: ReactNode) => (
              <span className="text-primary-800">{text}</span>
            ),
          }
        )}
      />
    </DataLoadingWrapper>
  );
};
